import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import api from "../lib/api";
import Newsletter from "../components/Newsletter";
import ExperienceCard from "../components/ExperienceCard";
import ParcoursCard from "../components/ParcoursCard";
import HeroCarousel from "../components/HeroCarousel";
import ManifesteBlock from "../components/ManifesteBlock";
import NouvelleVague from "../components/NouvelleVague";
import VoicesTicker from "../components/VoicesTicker";
import SceneSection from "../components/SceneSection";
import RichesseStrip from "../components/RichesseStrip";
import SEO, { orgLD, websiteLD } from "../components/SEO";

// Bespoke editorial images (generated for Côte Perdue, served from backend static)
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const IMG = {
    heroMain:    `${BACKEND}/api/static/img/hero_cliffs.jpg`,
    heroSide:    `${BACKEND}/api/static/img/villas_mers.jpg`,
    essayLarge:  `${BACKEND}/api/static/img/festival_dinner.jpg`,
    essaySmallA: `${BACKEND}/api/static/img/villa_interior.jpg`,
    essaySmallB: `${BACKEND}/api/static/img/antiquaire.jpg`,
    quoteBg:     `${BACKEND}/api/static/img/foret_eu.jpg`,
    vertLeft:    `${BACKEND}/api/static/img/chambre_hotes.jpg`,
    vertRight:   `${BACKEND}/api/static/img/hero_cliffs.jpg`,
    cartePreview:`${BACKEND}/api/static/img/vallee_bresle.jpg`,
    festival:    `${BACKEND}/api/static/img/festival_dinner.jpg`,
    essay3:      `${BACKEND}/api/static/img/restaurant_french.jpg`,
};

const formatDate = (iso) => new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });

export default function Home() {
    const [cities, setCities] = useState([]);
    const [events, setEvents] = useState([]);
    const [featuredPlaces, setFeaturedPlaces] = useState([]);
    const [hiddenPlaces, setHiddenPlaces] = useState([]);
    const [walks, setWalks] = useState([]);
    const [articles, setArticles] = useState([]);
    const [experiences, setExperiences] = useState([]);
    const [parcours, setParcours] = useState([]);

    useEffect(() => {
        Promise.all([
            api.get("/cities"),
            api.get("/events", { params: { upcoming: true, limit: 6 } }),
            api.get("/places", { params: { featured: true, limit: 6 } }),
            api.get("/places", { params: { hidden_gem: true, limit: 4 } }),
            api.get("/walks", { params: { limit: 4 } }),
            api.get("/articles", { params: { limit: 5 } }),
            api.get("/experiences", { params: { featured: true, limit: 6 } }),
            api.get("/parcours", { params: { featured: true, limit: 4 } }),
        ]).then(([cs, es, ps, hps, ws, as_, xps, pcs]) => {
            setCities(cs.data);
            setEvents(es.data);
            setFeaturedPlaces(ps.data);
            setHiddenPlaces(hps.data);
            setWalks(ws.data);
            setArticles(as_.data);
            setExperiences(xps.data);
            setParcours(pcs.data);
        }).catch(() => {});
    }, []);

    const signatureArticle = articles[0];

    return (
        <div data-testid="home-page" className="cp-above">
            <SEO
                title=""
                description="Côte Perdue — magazine et carnet d'adresses vérifiées entre Haute-Normandie et Picardie : Le Tréport, Mers-les-Bains, Cayeux-sur-Mer, Eu, Bois-de-Cise, Blangy-sur-Bresle, Aumale, Neufchâtel-en-Bray. 289 adresses notées Google ≥ 4,5/5, hôtels chic, randonnées, jardins remarquables."
                path="/"
                structuredData={[orgLD(), websiteLD()]}
            />

            {/* ================================================================
                 HERO — Cinematic 5-slide carousel (swipe/click)
                ================================================================ */}
            <HeroCarousel />

            {/* ================================================================
                 VOICES — insider whisper quotes ticker (bohème chic signature)
                ================================================================ */}
            <VoicesTicker />

            {/* ================================================================
                 RICHESSE — Numbers + 4 shortcuts (let visitors *see* the depth)
                ================================================================ */}
            <RichesseStrip />

            {/* ================================================================
                 RUNNING MARQUEE
                ================================================================ */}
            <section className="bg-cp-ivoire py-8 border-b border-cp-border overflow-hidden" data-testid="marquee">
                <div className="cp-marquee">
                    <div className="cp-marquee__track cp-serif text-5xl md:text-7xl text-cp-encre">
                        {Array.from({ length: 2 }).flatMap((_, i) => [
                            <span key={`t1-${i}`}>500 villas classées</span>,
                            <span key={`t2-${i}`} className="cp-display-italic text-cp-sable-deep">à Mers-les-Bains</span>,
                            <span key={`t3-${i}`}>—</span>,
                            <span key={`t4-${i}`}>9 300 hectares de forêt royale</span>,
                            <span key={`t5-${i}`} className="cp-display-italic text-cp-sable-deep">à 2h de Paris</span>,
                            <span key={`t6-${i}`}>—</span>,
                        ])}
                    </div>
                </div>
            </section>

            {/* ================================================================
                 NOUVELLE VAGUE — trend section, three pieces
                ================================================================ */}
            <NouvelleVague />

            {/* ================================================================
                 SCÈNE & SAVOIR-FAIRE — culture, underground teaser
                ================================================================ */}
            <SceneSection />

            {/* ================================================================
                 PHOTO ESSAY #1 — asymmetric triptych
                ================================================================ */}
            <section className="py-24 md:py-40 px-6 md:px-12 max-w-[1600px] mx-auto" data-testid="essay-1">
                <div className="grid grid-cols-12 gap-6 md:gap-10">
                    <figure className="col-span-12 md:col-span-7">
                        <div className="cp-image-zoom aspect-[5/6] md:aspect-[4/5] bg-cp-craie">
                            <img src={IMG.essayLarge} alt="" className="w-full h-full object-cover cp-photo-warm" />
                        </div>
                        <figcaption className="mt-4 flex items-start gap-4">
                            <span className="cp-tiny text-cp-sable-deep mt-1">Pl. 01</span>
                            <p className="cp-serif italic text-lg text-cp-gris leading-snug max-w-md">
                                Table posée sur un lin brut. Verres soufflés à quelques kilomètres, pain cuit au feu, vin du pays de Caux. On reste longtemps.
                            </p>
                        </figcaption>
                    </figure>

                    <div className="col-span-12 md:col-span-5 flex flex-col justify-end gap-10 md:gap-16 md:pt-40">
                        <figure>
                            <div className="cp-image-zoom aspect-[4/5] bg-cp-craie">
                                <img src={IMG.essaySmallA} alt="" className="w-full h-full object-cover cp-photo-warm" />
                            </div>
                            <figcaption className="mt-4 flex items-start gap-4">
                                <span className="cp-tiny text-cp-sable-deep mt-1">Pl. 02</span>
                                <p className="cp-serif italic text-base text-cp-gris leading-snug">
                                    Un couloir de villa Belle Époque. La lumière tombe comme en 1905.
                                </p>
                            </figcaption>
                        </figure>
                        <div className="border-l-2 border-cp-sable-deep pl-6">
                            <p className="cp-script text-2xl md:text-3xl text-cp-encre leading-tight mb-2">
                                « Ici, on ne joue rien. On vit. »
                            </p>
                            <p className="cp-tiny text-cp-gris">— Une hôtesse, Mers-les-Bains</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* ================================================================
                 DESTINATIONS — magazine spread
                ================================================================ */}
            <section className="py-24 md:py-40 bg-cp-ivoire-bright border-y border-cp-border" data-testid="home-destinations">
                <div className="max-w-[1600px] mx-auto px-6 md:px-12">
                    <div className="flex items-end justify-between mb-16 md:mb-24">
                        <div>
                            <p className="cp-tiny text-cp-foret mb-5">Chapitre I · Le territoire</p>
                            <h2 className="cp-serif text-6xl md:text-7xl lg:text-8xl leading-[0.9] text-cp-encre max-w-3xl">
                                Seize lieux,<br />
                                <em className="cp-display-italic">mille</em> lumières.
                            </h2>
                        </div>
                        <Link to="/destinations" className="cp-link hidden md:inline-block">Toutes les destinations</Link>
                    </div>

                    <div className="grid grid-cols-12 gap-6 md:gap-10">
                        {cities.map((city, idx) => {
                            const styles = [
                                "col-span-12 md:col-span-7",
                                "col-span-12 md:col-span-5 md:mt-24",
                                "col-span-12 md:col-span-5",
                                "col-span-12 md:col-span-7 md:-mt-12",
                            ];
                            const ratios = ["aspect-[4/5]", "aspect-[3/4]", "aspect-[3/4]", "aspect-[4/5]"];
                            return (
                                <Link
                                    to={`/destinations/${city.slug}`}
                                    key={city.id}
                                    data-testid={`destination-card-${city.slug}`}
                                    className={`${styles[idx] || "col-span-12 md:col-span-6"} group block`}
                                >
                                    <div className={`cp-image-zoom ${ratios[idx] || "aspect-[4/5]"} bg-cp-craie mb-5`}>
                                        <img src={city.hero_image} alt={city.name} loading="lazy" className="w-full h-full object-cover cp-photo-warm" />
                                    </div>
                                    <div className="flex items-baseline gap-4">
                                        <span className="cp-serif text-2xl italic text-cp-sable-deep">0{idx + 1}</span>
                                        <div>
                                            <h3 className="cp-serif text-4xl md:text-5xl leading-tight text-cp-encre group-hover:text-cp-foret transition-colors">
                                                {city.name}
                                            </h3>
                                            <p className="cp-serif text-xl italic text-cp-gris mt-1">{city.tagline}</p>
                                        </div>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ================================================================
                 EXPERIENCES — exclusive escapades
                ================================================================ */}
            <section className="py-24 md:py-40 px-6 md:px-12 max-w-[1600px] mx-auto" data-testid="home-experiences">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16 md:mb-20">
                    <div className="md:col-span-6">
                        <p className="cp-tiny text-cp-foret mb-5">Chapitre VII · Expériences d'exception</p>
                        <h2 className="cp-serif text-6xl md:text-7xl lg:text-8xl leading-[0.9] text-cp-encre">
                            Des expériences,<br /><em className="cp-display-italic">vérifiées.</em>
                        </h2>
                    </div>
                    <p className="md:col-span-5 md:col-start-8 cp-serif text-xl md:text-2xl italic font-light text-cp-gris leading-relaxed self-end">
                        Musées classés, jardins remarquables, châteaux ouverts, artisans qui travaillent. Vingt expériences 100% vérifiées, chacune avec sa source officielle.
                    </p>
                </div>
                <div className="grid grid-cols-12 gap-6 md:gap-10">
                    {experiences.slice(0, 6).map((xp, idx) => {
                        const layouts = [
                            "col-span-12 md:col-span-5",
                            "col-span-12 md:col-span-7 md:mt-20",
                            "col-span-12 md:col-span-4 md:mt-12",
                            "col-span-12 md:col-span-4",
                            "col-span-12 md:col-span-4 md:mt-16",
                            "col-span-12 md:col-span-6 md:col-start-4",
                        ];
                        return (
                            <div key={xp.id} className={layouts[idx % 6]}>
                                <ExperienceCard experience={xp} index={idx} />
                            </div>
                        );
                    })}
                </div>
                <div className="mt-16">
                    <Link to="/experiences" className="cp-link" data-testid="all-experiences-cta">Toutes les expériences →</Link>
                </div>
            </section>

            {/* ================================================================
                 PARCOURS — multi-stop itineraries
                ================================================================ */}
            <section className="py-24 md:py-40 bg-cp-encre text-cp-ivoire cp-grain relative" data-testid="home-parcours">
                <div className="max-w-[1600px] mx-auto px-6 md:px-12">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16 md:mb-20">
                        <div className="md:col-span-6">
                            <p className="cp-tiny text-cp-sable mb-5">Chapitre VIII · Parcours</p>
                            <h2 className="cp-serif text-6xl md:text-7xl lg:text-8xl leading-[0.9] text-cp-ivoire">
                                Verres,<br /><em className="cp-display-italic text-cp-sable">jardins,</em> brocantes.
                            </h2>
                        </div>
                        <p className="md:col-span-5 md:col-start-8 cp-serif text-xl md:text-2xl italic font-light text-cp-ivoire/75 leading-relaxed self-end">
                            Des itinéraires cousus main. Trois, quatre, sept haltes — pour une journée, un week-end, un dimanche parfait.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16">
                        {parcours.slice(0, 4).map((p, i) => (
                            <Link key={p.id} to={`/parcours/${p.slug}`} data-testid={`home-parcours-${p.slug}`} className="group block">
                                <div className="cp-image-zoom aspect-[16/10] bg-cp-encre-warm mb-6">
                                    <img src={p.image.startsWith("http") ? p.image : `${process.env.REACT_APP_BACKEND_URL}${p.image}`} alt={p.title} loading="lazy" className="w-full h-full object-cover cp-photo-warm" />
                                </div>
                                <div className="flex items-baseline gap-4">
                                    <span className="cp-serif italic text-2xl text-cp-sable">{String(i + 1).padStart(2, "0")}</span>
                                    <div>
                                        <p className="cp-tiny text-cp-sable mb-2">{p.theme} · {p.duration}</p>
                                        <h3 className="cp-serif text-3xl md:text-4xl leading-tight text-cp-ivoire group-hover:italic transition-all mb-2">{p.title}</h3>
                                        <p className="cp-serif italic text-base text-cp-ivoire/70 leading-snug">{p.tagline}</p>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                    <div className="mt-16">
                        <Link to="/parcours" className="cp-link cp-link--light" data-testid="all-parcours-cta">Tous les parcours →</Link>
                    </div>
                </div>
            </section>

            {/* ================================================================
                 AGENDA — newspaper-style list
                ================================================================ */}
            <section className="py-24 md:py-40 px-6 md:px-12 max-w-[1600px] mx-auto" data-testid="home-events">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16">
                    <div className="md:col-span-5">
                        <p className="cp-tiny text-cp-foret mb-5">Chapitre III · Agenda</p>
                        <h2 className="cp-serif text-6xl md:text-7xl lg:text-8xl leading-[0.9] text-cp-encre">
                            À <em className="cp-display-italic">l'affiche</em>.
                        </h2>
                    </div>
                    <p className="md:col-span-6 md:col-start-7 cp-serif text-xl md:text-2xl italic font-light text-cp-gris leading-relaxed self-end">
                        Ce qui vaut le déplacement, ces prochaines semaines. Pas tout — seulement ce qui tient la promesse.
                    </p>
                </div>

                <div className="border-t-2 border-cp-encre">
                    {events.slice(0, 5).map((ev, idx) => (
                        <Link
                            to={`/evenements/${ev.slug}`}
                            key={ev.id}
                            data-testid={`event-row-${ev.slug}`}
                            className="group grid grid-cols-12 gap-4 md:gap-8 py-6 md:py-8 border-b border-cp-border items-center hover:bg-cp-craie transition-colors px-2 md:px-4 -mx-2 md:-mx-4"
                        >
                            <span className="col-span-2 md:col-span-1 cp-mono text-cp-sable-deep text-sm">0{idx + 1}</span>
                            <span className="col-span-10 md:col-span-2 cp-mono text-xs md:text-sm text-cp-encre">{formatDate(ev.start_date)}</span>
                            <div className="col-span-12 md:col-span-6">
                                <h3 className="cp-serif text-2xl md:text-4xl leading-tight text-cp-encre group-hover:italic transition-all">
                                    {ev.title}
                                </h3>
                                <p className="cp-mono text-xs text-cp-gris mt-1">{ev.location_name}</p>
                            </div>
                            <span className="col-span-8 md:col-span-2 cp-serif italic text-cp-foret text-sm md:text-base">{ev.category.replace(/-/g, " ")}</span>
                            <span className="col-span-4 md:col-span-1 cp-tiny text-right text-cp-encre opacity-0 group-hover:opacity-100 transition-opacity">→</span>
                        </Link>
                    ))}
                </div>

                <div className="mt-10">
                    <Link to="/evenements" className="cp-link" data-testid="events-all-cta">Tout l'agenda</Link>
                </div>
            </section>

            {/* ================================================================
                 HIDDEN / DARK SECTION — warm black, film grain, editorial
                ================================================================ */}
            <section className="relative py-28 md:py-40 bg-cp-encre-warm text-cp-ivoire cp-grain overflow-hidden" data-testid="home-hidden">
                <div className="max-w-[1600px] mx-auto px-6 md:px-12">
                    <div className="grid grid-cols-12 gap-10 mb-20">
                        <div className="col-span-12 md:col-span-6">
                            <p className="cp-tiny text-cp-sable mb-5">Chapitre II · Sélection</p>
                            <h2 className="cp-serif text-6xl md:text-7xl lg:text-8xl leading-[0.9] text-cp-ivoire">
                                Les adresses<br />
                                <em className="cp-display-italic text-cp-sable">qu'on aime.</em>
                            </h2>
                        </div>
                        <p className="col-span-12 md:col-span-5 md:col-start-8 cp-serif text-xl md:text-2xl italic font-light text-cp-ivoire/70 leading-relaxed self-end">
                            Quatre adresses choisies par la rédaction — tables, maisons d'hôtes, lieux à voir. Vérifiées, visitables.
                        </p>
                    </div>

                    <div className="grid grid-cols-12 gap-6 md:gap-10">
                        {hiddenPlaces.map((p, idx) => {
                            const layouts = [
                                "col-span-12 md:col-span-6",
                                "col-span-12 md:col-span-5 md:col-start-8 md:mt-32",
                                "col-span-12 md:col-span-5 md:-mt-16",
                                "col-span-12 md:col-span-6 md:col-start-7",
                            ];
                            const ratios = ["aspect-[4/5]", "aspect-[3/4]", "aspect-[3/4]", "aspect-[5/6]"];
                            return (
                                <Link
                                    to={`/adresses/${p.slug}`}
                                    key={p.id}
                                    data-testid={`hidden-place-${p.slug}`}
                                    className={`${layouts[idx]} group block`}
                                >
                                    <div className={`cp-image-zoom ${ratios[idx]} bg-cp-encre mb-5`}>
                                        <img src={p.image} alt={p.name} loading="lazy" className="w-full h-full object-cover cp-photo-dark opacity-95 group-hover:opacity-100 transition-opacity" />
                                    </div>
                                    <div className="flex items-baseline gap-4">
                                        <span className="cp-tiny text-cp-sable">N° {String(idx + 1).padStart(2, "0")}</span>
                                        <div>
                                            <h3 className="cp-serif text-3xl md:text-4xl leading-tight text-cp-ivoire group-hover:text-cp-sable transition-colors">
                                                {p.name}
                                            </h3>
                                            <p className="cp-serif italic text-base text-cp-ivoire/60 mt-1.5 leading-snug max-w-sm">{p.short_description}</p>
                                        </div>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>

                    <div className="mt-24 flex items-end justify-between">
                        <p className="cp-script text-3xl md:text-4xl text-cp-sable max-w-md leading-tight">
                            — À découvrir sans attendre.
                        </p>
                        <Link to="/adresses" className="cp-link cp-link--light">Toutes les adresses</Link>
                    </div>
                </div>
            </section>

            {/* ================================================================
                 VERTICAL PAIR — photo essay #2
                ================================================================ */}
            <section className="py-24 md:py-40 px-6 md:px-12 max-w-[1600px] mx-auto" data-testid="vertical-pair">
                <div className="grid grid-cols-12 gap-6 md:gap-12">
                    <figure className="col-span-12 md:col-span-5">
                        <div className="cp-image-zoom aspect-[3/4] bg-cp-craie">
                            <img src={IMG.vertLeft} alt="" className="w-full h-full object-cover cp-photo-warm" />
                        </div>
                        <figcaption className="cp-serif italic text-cp-gris text-base mt-4 flex items-start gap-3">
                            <span className="cp-tiny text-cp-sable-deep mt-1">Pl. 03</span>
                            Intérieur d'une maison d'hôtes à Mers. Six chambres, cinq bibliothèques.
                        </figcaption>
                    </figure>
                    <div className="col-span-12 md:col-span-2 flex items-center justify-center py-10 md:py-0">
                        <p className="cp-vertical text-cp-sable-deep text-base md:text-lg">
                            La lumière du nord, plus douce qu'on ne croit
                        </p>
                    </div>
                    <figure className="col-span-12 md:col-span-5 md:mt-24">
                        <div className="cp-image-zoom aspect-[3/4] bg-cp-craie">
                            <img src={IMG.vertRight} alt="" className="w-full h-full object-cover cp-photo-warm" />
                        </div>
                        <figcaption className="cp-serif italic text-cp-gris text-base mt-4 flex items-start gap-3">
                            <span className="cp-tiny text-cp-sable-deep mt-1">Pl. 04</span>
                            Falaises du Tréport, 7h du matin. On redescend à pied.
                        </figcaption>
                    </figure>
                </div>
            </section>

            {/* ================================================================
                 BIG QUOTE BAND
                ================================================================ */}
            <section className="relative py-40 md:py-56 overflow-hidden cp-grain" data-testid="quote-band">
                <div className="absolute inset-0">
                    <img src={IMG.quoteBg} alt="" className="w-full h-full object-cover cp-photo-dark" />
                    <div className="absolute inset-0 bg-cp-encre/55" />
                </div>
                <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 text-cp-ivoire">
                    <p className="cp-tiny text-cp-sable mb-12">Monologue · Extrait</p>
                    <blockquote className="cp-serif text-4xl sm:text-5xl md:text-7xl lg:text-8xl leading-[1.02] font-light max-w-5xl">
                        <em className="cp-display-italic">« </em>Il existe encore en France<br />
                        des côtes qui ne se donnent<br />
                        <em className="cp-display-italic">pas</em> au premier regard.<em className="cp-display-italic"> »</em>
                    </blockquote>
                    <p className="cp-tiny text-cp-ivoire/70 mt-12">— Dossier N° 01, à paraître</p>
                </div>
            </section>

            {/* ================================================================
                 WALKS — numbered, minimal
                ================================================================ */}
            <section className="py-24 md:py-40 px-6 md:px-12 max-w-[1600px] mx-auto" data-testid="home-walks">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16">
                    <div className="md:col-span-5">
                        <p className="cp-tiny text-cp-foret mb-5">Chapitre IV · Balades</p>
                        <h2 className="cp-serif text-6xl md:text-7xl lg:text-8xl leading-[0.9] text-cp-encre">
                            Marcher,<br />
                            <em className="cp-display-italic">comme on</em> écrit.
                        </h2>
                    </div>
                    <p className="md:col-span-6 md:col-start-7 cp-serif text-xl md:text-2xl italic font-light text-cp-gris leading-relaxed self-end">
                        Cinq itinéraires, cousus à la main, pour chaque heure — et chaque humeur — de la journée.
                    </p>
                </div>
                <div className="grid grid-cols-12 gap-6 md:gap-10">
                    {walks.slice(0, 4).map((w, idx) => {
                        const layouts = [
                            "col-span-12 md:col-span-5",
                            "col-span-12 md:col-span-4 md:mt-20",
                            "col-span-12 md:col-span-3 md:mt-10",
                            "col-span-12 md:col-span-6 md:col-start-4",
                        ];
                        return (
                            <Link
                                to={`/balades/${w.slug}`}
                                key={w.id}
                                data-testid={`walk-card-${w.slug}`}
                                className={`${layouts[idx]} group block`}
                            >
                                <div className="cp-image-zoom aspect-[4/5] bg-cp-craie mb-4">
                                    <img src={w.hero_image || w.image} alt={w.title} loading="lazy" className="w-full h-full object-cover cp-photo-warm" />
                                </div>
                                <div className="flex items-baseline gap-3">
                                    <span className="cp-serif text-2xl italic text-cp-sable-deep">N°{idx + 1}</span>
                                    <h3 className="cp-serif text-2xl md:text-3xl leading-tight text-cp-encre group-hover:italic transition-all">
                                        {w.title}
                                    </h3>
                                </div>
                                <p className="cp-mono text-xs text-cp-gris mt-3">{w.distance_km} km · {w.duration_minutes} min · {w.difficulty}</p>
                            </Link>
                        );
                    })}
                </div>
            </section>

            {/* ================================================================
                 CARTE TEASER
                ================================================================ */}
            <section className="py-24 md:py-40 bg-cp-ivoire-bright border-y border-cp-border" data-testid="home-map-teaser">
                <div className="max-w-[1600px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-10 items-center">
                    <div className="col-span-12 md:col-span-5 md:pr-12">
                        <p className="cp-tiny text-cp-foret mb-5">Cartographie</p>
                        <h2 className="cp-serif text-6xl md:text-7xl lg:text-8xl leading-[0.9] text-cp-encre mb-8">
                            Une côte,<br /><em className="cp-display-italic">cent</em> regards.
                        </h2>
                        <p className="cp-serif text-xl italic font-light text-cp-gris leading-relaxed mb-10 max-w-md">
                            Une carte pensée pour les flâneurs, les curieux, les collectionneurs de lumières. Adresses, événements, balades — tout, calé au millimètre.
                        </p>
                        <Link to="/carte" data-testid="map-teaser-cta" className="cp-btn-ghost">Ouvrir la carte →</Link>
                    </div>
                    <div className="col-span-12 md:col-span-7">
                        <figure>
                            <div className="aspect-[5/4] overflow-hidden bg-cp-craie border border-cp-border">
                                <img src={IMG.cartePreview} alt="Aperçu carte" className="w-full h-full object-cover cp-photo-warm" />
                            </div>
                            <figcaption className="cp-tiny text-cp-sable-deep mt-3">Fig. 01 · Vue d'ensemble · 1/50 000</figcaption>
                        </figure>
                    </div>
                </div>
            </section>

            {/* ================================================================
                 SIGNATURE ARTICLE
                ================================================================ */}
            {signatureArticle && (
                <section className="py-24 md:py-40 px-6 md:px-12 max-w-[1400px] mx-auto" data-testid="home-signature-article">
                    <p className="cp-tiny text-cp-foret text-center mb-10">Chapitre V · Article signature</p>
                    <Link to={`/magazine/${signatureArticle.slug}`} data-testid={`article-card-large-${signatureArticle.slug}`} className="group block">
                        <div className="cp-image-zoom aspect-[16/10] overflow-hidden bg-cp-craie mb-10">
                            <img src={signatureArticle.cover_image} alt={signatureArticle.title} loading="lazy" className="w-full h-full object-cover cp-photo-warm" />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
                            <div className="md:col-span-8">
                                <p className="cp-tiny text-cp-sable-deep mb-4">{signatureArticle.category} · {signatureArticle.reading_time} min de lecture</p>
                                <h2 className="cp-serif text-5xl md:text-6xl lg:text-7xl leading-[0.95] text-cp-encre group-hover:italic transition-all">
                                    {signatureArticle.title}
                                </h2>
                            </div>
                            <div className="md:col-span-4 flex flex-col justify-end">
                                <p className="cp-serif text-xl italic font-light text-cp-gris leading-snug mb-5">{signatureArticle.subtitle}</p>
                                <p className="cp-link">Lire l'article <ArrowUpRight size={14} strokeWidth={1.25} className="inline -mt-1" /></p>
                            </div>
                        </div>
                    </Link>
                </section>
            )}

            {/* ================================================================
                 TABLES & MAISONS (featured places) — magazine ledger
                ================================================================ */}
            <section className="py-24 md:py-40 bg-cp-craie" data-testid="home-places">
                <div className="max-w-[1600px] mx-auto px-6 md:px-12">
                    <div className="flex items-end justify-between mb-16">
                        <div>
                            <p className="cp-tiny text-cp-foret mb-5">Ledger · Tables & Maisons</p>
                            <h2 className="cp-serif text-6xl md:text-7xl lg:text-8xl leading-[0.9] text-cp-encre">
                                Celles qui <em className="cp-display-italic">valent</em><br />le détour.
                            </h2>
                        </div>
                        <Link to="/adresses" className="cp-link hidden md:inline-block">Toutes les adresses</Link>
                    </div>
                    <div className="grid grid-cols-12 gap-6 md:gap-10">
                        {featuredPlaces.slice(0, 6).map((p, idx) => {
                            const layouts = [
                                "col-span-12 md:col-span-6",
                                "col-span-12 md:col-span-6",
                                "col-span-12 md:col-span-4 md:mt-12",
                                "col-span-12 md:col-span-4 md:mt-12",
                                "col-span-12 md:col-span-4 md:mt-12",
                                "col-span-12 md:col-span-7",
                            ];
                            const ratios = ["aspect-[3/4]", "aspect-[3/4]", "aspect-[4/5]", "aspect-[4/5]", "aspect-[4/5]", "aspect-[5/4]"];
                            return (
                                <Link
                                    to={`/adresses/${p.slug}`}
                                    key={p.id}
                                    data-testid={`place-card-${p.slug}`}
                                    className={`${layouts[idx]} group block`}
                                >
                                    <div className={`cp-image-zoom ${ratios[idx]} bg-cp-ivoire-bright mb-4`}>
                                        <img src={p.image} alt={p.name} loading="lazy" className="w-full h-full object-cover cp-photo-warm" />
                                    </div>
                                    <div className="flex items-start justify-between gap-4">
                                        <div>
                                            <p className="cp-tiny text-cp-sable-deep mb-1.5">{p.category.replace(/-/g, " ")}</p>
                                            <h3 className="cp-serif text-3xl md:text-4xl leading-tight text-cp-encre group-hover:italic transition-all">
                                                {p.name}
                                            </h3>
                                            <p className="cp-serif italic text-base text-cp-gris mt-2 max-w-md leading-snug">{p.short_description}</p>
                                        </div>
                                        {p.price_range && <span className="cp-mono text-sm text-cp-foret mt-2">{p.price_range}</span>}
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ================================================================
                 MANIFESTE — moved here so visitors first see the territory's
                 richness before absorbing the editorial soul
                ================================================================ */}
            <ManifesteBlock />

            {/* ================================================================
                 EDITORIAL NOTE (handwritten signature)
                ================================================================ */}
            <section className="py-20 md:py-28 px-6 md:px-12 max-w-[1400px] mx-auto" data-testid="editorial-note">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
                    <div className="md:col-span-3">
                        <p className="cp-tiny text-cp-sable-deep mb-3">Note de la rédaction</p>
                        <p className="cp-mono text-cp-gris">N° 01 · Hiver 2026</p>
                    </div>
                    <div className="md:col-span-7 md:col-start-5">
                        <p className="cp-serif text-3xl md:text-4xl lg:text-5xl leading-[1.2] text-cp-encre font-light">
                            Nous avons cherché une côte<br />
                            <em className="cp-display-italic text-cp-foret">qui respire encore.</em><br />
                            Nous l'avons trouvée.
                        </p>
                        <p className="cp-script text-2xl md:text-3xl text-cp-sable-deep mt-10 leading-snug transform -rotate-1 inline-block">
                            — La rédaction, Eu, janvier
                        </p>
                    </div>
                </div>
            </section>

            {/* ================================================================
                 BOTTOM — weekend builder + newsletter signature
                ================================================================ */}
            <section className="py-24 md:py-40 px-6 md:px-12 max-w-[1600px] mx-auto" data-testid="home-bottom">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-16 md:gap-24">
                    <div className="md:col-span-7">
                        <p className="cp-tiny text-cp-foret mb-5">Concierge</p>
                        <h2 className="cp-serif text-5xl md:text-6xl lg:text-7xl leading-[0.95] text-cp-encre mb-8">
                            Dites-nous qui<br />
                            vous êtes. Nous<br />
                            <em className="cp-display-italic text-cp-foret">dessinons</em> le reste.
                        </h2>
                        <p className="cp-serif italic text-xl font-light text-cp-gris leading-relaxed max-w-xl mb-10">
                            Trois minutes de questions — un itinéraire composé pour vous, pas pour personne.
                        </p>
                        <Link to="/week-end" data-testid="bottom-weekend-cta" className="cp-btn-primary">Composer mon week-end →</Link>
                    </div>
                    <div className="md:col-span-4 md:col-start-9 border-l border-cp-border pl-8 md:pl-12 flex flex-col justify-end">
                        <p className="cp-tiny text-cp-foret mb-5">La lettre</p>
                        <h3 className="cp-serif text-4xl md:text-5xl leading-[0.95] text-cp-encre mb-6">
                            Une <em className="cp-display-italic">lettre</em>. Par mois.
                        </h3>
                        <p className="cp-script text-2xl text-cp-sable-deep mb-6">Rien d'autre.</p>
                        <Newsletter variant="light" source="home-bottom" />
                    </div>
                </div>
            </section>

        </div>
    );
}
